<script setup>
import { ref, computed, h } from 'vue'
import { Plus, X, Check, Filter, Globe, MapPin, Layers, Sparkles, Monitor } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command'

// Default icons for common filter types
const defaultIcons = {
    platform: Monitor,
    platform_id: Monitor,
    content_type: Layers,
    content_types: Layers,
    is_sponsored: Sparkles,
    sponsored: Sparkles,
    language: Globe,
    creator_language: Globe,
    country: MapPin,
    creator_country: MapPin,
}

const props = defineProps({
    table: {
        type: Object,
        required: true,
    },
    filterFields: {
        type: Array,
        default: () => [],
    },
})

const isOpen = ref(false)
const activeFilterField = ref(null)

// Track which filter badge popover is open for inline editing
const editingFilterField = ref(null)

// Get only filterable fields (ones with options)
const filterableFields = computed(() => {
    return props.filterFields.filter(field => field.options && field.options.length > 0)
})

// Get icon for a filter field
const getFieldIcon = (field) => {
    // Use custom icon if provided
    if (field.icon) return field.icon
    // Fall back to default icons based on field value
    return defaultIcons[field.value] || Filter
}

// Get column for a filter field (may not exist for server-side only filters)
const getColumn = (fieldValue) => {
    return props.table.getColumn(fieldValue)
}

// Get filter value from table state (works even without column)
const getFilterValue = (fieldValue) => {
    const columnFilters = props.table.getState().columnFilters || []
    const filter = columnFilters.find(f => f.id === fieldValue)
    return filter?.value || []
}

// Set filter value in table state (works even without column)
const setFilterValue = (fieldValue, values) => {
    const columnFilters = [...(props.table.getState().columnFilters || [])]
    const existingIndex = columnFilters.findIndex(f => f.id === fieldValue)

    if (values && values.length > 0) {
        if (existingIndex >= 0) {
            columnFilters[existingIndex] = { id: fieldValue, value: values }
        } else {
            columnFilters.push({ id: fieldValue, value: values })
        }
    } else {
        if (existingIndex >= 0) {
            columnFilters.splice(existingIndex, 1)
        }
    }

    props.table.setColumnFilters(columnFilters)
}

// Get active filters with their selected values
const activeFilters = computed(() => {
    return filterableFields.value
        .map(field => {
            const filterValue = getFilterValue(field.value)
            if (!filterValue || filterValue.length === 0) return null

            return {
                field,
                selectedValues: filterValue,
                selectedLabels: filterValue.map(val => {
                    const option = field.options.find(o => o.value === val)
                    return option?.label || val
                }),
            }
        })
        .filter(Boolean)
})

const hasActiveFilters = computed(() => activeFilters.value.length > 0)

// Toggle a filter value
const toggleFilterValue = (field, value) => {
    let filterValues = getFilterValue(field.value)

    if (filterValues.includes(value)) {
        filterValues = filterValues.filter(v => v !== value)
    } else {
        filterValues = [...filterValues, value]
    }

    setFilterValue(field.value, filterValues)

    // Close edit popover if filter is now empty (compare field object's value property)
    if (filterValues.length === 0 && editingFilterField.value?.value === field.value) {
        editingFilterField.value = null
    }
}

// Check if a value is selected
const isValueSelected = (field, value) => {
    const filterValues = getFilterValue(field.value)
    return filterValues.includes(value)
}

// Remove a filter entirely
const removeFilter = (field) => {
    setFilterValue(field.value, undefined)
}

// Clear all filters
const clearAllFilters = () => {
    filterableFields.value.forEach(field => {
        setFilterValue(field.value, undefined)
    })
}

// Go back to field selection
const goBack = () => {
    activeFilterField.value = null
}
</script>

<template>
    <div class="flex shrink-0 items-center gap-1.5">
        <!-- Active filter badges with inline edit popovers -->
        <Popover
            v-for="filter in activeFilters"
            :key="filter.field.value"
            :open="editingFilterField?.value === filter.field.value"
            @update:open="(open) => editingFilterField = open ? filter.field : null"
        >
            <PopoverTrigger as-child>
                <Badge
                    variant="secondary"
                    class="h-8 shrink-0 gap-1.5 pr-1.5 font-normal text-sm rounded-md cursor-pointer hover:bg-secondary/80 transition-colors whitespace-nowrap"
                >
                    <span class="text-muted-foreground">{{ filter.field.label }}:</span>
                    <span class="font-medium">{{ filter.selectedLabels.length > 2 ? `${filter.selectedLabels.length} selected` : filter.selectedLabels.join(', ') }}</span>
                    <button
                        class="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                        @click.stop="removeFilter(filter.field)"
                    >
                        <X class="h-3.5 w-3.5" />
                    </button>
                </Badge>
            </PopoverTrigger>
            <PopoverContent class="w-[220px] p-0" align="start">
                <!-- Inline filter editor -->
                <div class="flex flex-col">
                    <!-- Header with filter name -->
                    <div class="flex items-center gap-2 border-b px-3 py-2">
                        <component :is="getFieldIcon(filter.field)" class="h-4 w-4 text-muted-foreground" />
                        <span class="text-sm font-medium">{{ filter.field.label }}</span>
                    </div>

                    <!-- Options list with checkboxes -->
                    <div class="max-h-[250px] overflow-y-auto p-1">
                        <div
                            v-for="option in filter.field.options"
                            :key="option.value"
                            class="flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer hover:bg-accent"
                            @click="toggleFilterValue(filter.field, option.value)"
                        >
                            <div
                                :class="[
                                    'flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                                    isValueSelected(filter.field, option.value)
                                        ? 'bg-primary text-primary-foreground'
                                        : 'opacity-50',
                                ]"
                            >
                                <Check v-if="isValueSelected(filter.field, option.value)" class="h-3 w-3" />
                            </div>
                            <span class="text-sm">{{ option.label }}</span>
                        </div>
                    </div>

                    <!-- Clear filter action -->
                    <div class="border-t p-1">
                        <button
                            class="w-full text-center text-sm py-1.5 rounded-sm hover:bg-accent text-muted-foreground"
                            @click="removeFilter(filter.field); editingFilterField = null"
                        >
                            Clear filter
                        </button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>

        <!-- Filter button -->
        <Popover
            v-model:open="isOpen"
            @update:open="(open) => { if (!open) activeFilterField = null }"
        >
            <PopoverTrigger as-child>
                <Button
                    variant="outline"
                    size="sm"
                    class="h-8 shrink-0 gap-1.5 border-dashed text-muted-foreground hover:text-foreground"
                >
                    <Plus class="h-4 w-4" />
                    Filter
                </Button>
            </PopoverTrigger>
            <PopoverContent class="w-[220px] p-0" align="start">
                <Command>
                    <!-- Field selection view -->
                    <template v-if="!activeFilterField">
                        <CommandInput placeholder="Filter by..." class="h-9 border-0 focus:ring-0 focus-visible:ring-0" />
                        <CommandList>
                            <CommandEmpty>No filters found.</CommandEmpty>
                            <CommandGroup>
                                <CommandItem
                                    v-for="field in filterableFields"
                                    :key="field.value"
                                    :value="field.label"
                                    @select="activeFilterField = field"
                                    class="cursor-pointer gap-2"
                                >
                                    <component :is="getFieldIcon(field)" class="h-4 w-4 text-muted-foreground" />
                                    {{ field.label }}
                                    <Badge
                                        v-if="getFilterValue(field.value)?.length"
                                        variant="secondary"
                                        class="ml-auto h-5 px-1.5 text-xs"
                                    >
                                        {{ getFilterValue(field.value)?.length }}
                                    </Badge>
                                </CommandItem>
                            </CommandGroup>
                            <template v-if="hasActiveFilters">
                                <CommandSeparator />
                                <CommandGroup>
                                    <CommandItem
                                        value="__clear_all_filters__"
                                        @select="clearAllFilters"
                                        class="justify-center text-center cursor-pointer"
                                    >
                                        Clear all filters
                                    </CommandItem>
                                </CommandGroup>
                            </template>
                        </CommandList>
                    </template>

                    <!-- Value selection view -->
                    <template v-else>
                        <div class="flex items-center gap-2 border-b px-3 py-2">
                            <button
                                class="text-muted-foreground hover:text-foreground"
                                @click="goBack"
                            >
                                ←
                            </button>
                            <component :is="getFieldIcon(activeFilterField)" class="h-4 w-4 text-muted-foreground" />
                            <span class="text-sm font-medium">{{ activeFilterField.label }}</span>
                        </div>
                        <CommandList>
                            <CommandGroup>
                                <CommandItem
                                    v-for="option in activeFilterField.options"
                                    :key="option.value"
                                    :value="option.value"
                                    @select="toggleFilterValue(activeFilterField, option.value)"
                                    class="cursor-pointer"
                                >
                                    <div
                                        :class="[
                                            'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                                            isValueSelected(activeFilterField, option.value)
                                                ? 'bg-primary text-primary-foreground'
                                                : 'opacity-50 [&_svg]:invisible',
                                        ]"
                                    >
                                        <Check class="h-4 w-4" />
                                    </div>
                                    {{ option.label }}
                                </CommandItem>
                            </CommandGroup>
                            <template v-if="getFilterValue(activeFilterField.value)?.length">
                                <CommandSeparator />
                                <CommandGroup>
                                    <CommandItem
                                        value="__clear_filter__"
                                        @select="removeFilter(activeFilterField); goBack()"
                                        class="justify-center text-center cursor-pointer"
                                    >
                                        Clear filter
                                    </CommandItem>
                                </CommandGroup>
                            </template>
                        </CommandList>
                    </template>
                </Command>
            </PopoverContent>
        </Popover>
    </div>
</template>

